import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// project-imports
import Routes from 'routes';
import ThemeCustomization from 'themes';

import Loader from 'components/Loader';
import Locales from 'components/Locales';
import RTLLayout from 'components/RTLLayout';
import ScrollTop from 'components/ScrollTop';
import Snackbar from 'components/@extended/Snackbar';
import Notistack from 'components/third-party/Notistack';

// action-manager
import handleAction from 'utils/action-manager/actionManager';
import { APIAction } from 'utils/action-manager/api/apiManager';
import { runStorageMaintenance } from 'utils/storageMaintenance';
import { bootThirdPartyScripts } from 'utils/thirdPartyBoot';

// import { dispatch } from 'store';
// import { fetchMenu } from 'store/reducers/menu';

// offer-provider
import { OfferProvider } from 'contexts/OfferContext';
// dt-development-provider
import { DTDevelopmentProvider } from 'contexts/DTDevelopmentContext';

// auth-provider
import { AWSCognitoProvider as AuthProvider } from 'contexts/AWSCognitoContext';

// third-party
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { APIProvider } from '@vis.gl/react-google-maps';

import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css';
import 'leaflet-defaulticon-compatibility';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// ==============================|| ERROR BOUNDARY  ||============================== //

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const CHUNK_RELOAD_STORAGE_KEY = 'hh:chunk-load-reload-attempted';

const isChunkLoadError = (error: Error | null) => {
  const message = error?.message || '';
  const name = error?.name || '';

  return (
    name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
};

const getElementLabel = (element: Element | null) => {
  if (!element) return '';

  const id = element.id ? `#${element.id}` : '';
  const className =
    typeof element.className === 'string'
      ? element.className
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 3)
          .map((name) => `.${name}`)
          .join('')
      : '';

  return `${element.tagName.toLowerCase()}${id}${className}`;
};

const getDomErrorContext = () => {
  const root = document.getElementById('root');

  return {
    activeElement: getElementLabel(document.activeElement),
    bodyChildren: Array.from(document.body.children).map(getElementLabel).join(' > '),
    rootChildren: root ? Array.from(root.children).map(getElementLabel).join(' > ') : '',
    scripts: Array.from(document.scripts)
      .map((script) => script.id || script.src)
      .filter(Boolean)
      .slice(-12)
      .join(' | ')
  };
};

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const chunkLoadError = isChunkLoadError(error);
    const chunkReloadAttempted = sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) === 'true';
    const domContext = getDomErrorContext();

    // Aquí puedes usar console.error, Sentry, etc.
    console.error('💥 Caught by ErrorBoundary:', {
      boundaryName: 'AppErrorBoundary',
      error,
      errorInfo,
      location: window.location.href,
      userAgent: navigator.userAgent,
      chunkLoadError,
      chunkReloadAttempted,
      domContext
    });

    const errorData = {
      type: 'react-boundary',
      boundaryname: 'AppErrorBoundary',
      message: error.message,
      name: error.name,
      stack: error.stack,
      componentstack: errorInfo.componentStack,
      location: window.location.href,
      pathname: window.location.pathname,
      useragent: navigator.userAgent,
      chunkloaderror: chunkLoadError,
      chunkreloadattempted: chunkReloadAttempted,
      domcontext: JSON.stringify(domContext)
    };

    const apiAction: APIAction = {
      type: 'api',
      entity: 'log-error',
      method: 'create',
      data: errorData
    };

    handleAction(apiAction)
      .then((res) => {
        console.log('[ErrorBoundary] Error logged:', res);
      })
      .catch((err) => {
        console.error('[ErrorBoundary] Error logging failed:', err);
      });

    if (chunkLoadError && !chunkReloadAttempted) {
      sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, 'true');
      window.setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  render() {
    if (this.state.hasError) {
      const chunkLoadError = isChunkLoadError(this.state.error);

      const handleFullReset = () => {
        // 🔹 Borrar cookies
        document.cookie.split(';').forEach((c) => {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
        });

        // 🔹 Borrar localStorage y sessionStorage
        localStorage.clear();
        sessionStorage.clear();

        // 🔄 Recargar
        window.location.reload();
      };

      const handleRefresh = () => {
        window.location.reload();
      };

      return (
        <div
          style={{
            padding: '2rem',
            color: '#FFFFFF',
            backgroundColor: '#673dfe',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <h1>{chunkLoadError ? 'Update required' : 'Something went wrong...'}</h1>
          <p>{chunkLoadError ? 'We could not load the latest version of this page.' : 'We are working to fix it!'}</p>
          {chunkLoadError && (
            <button
              onClick={handleRefresh}
              style={{
                marginTop: '1.5rem',
                padding: '0.8rem 1.2rem',
                fontSize: '1rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#fff',
                color: '#673dfe',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Refresh
            </button>
          )}
          <button
            onClick={handleFullReset}
            style={{
              marginTop: chunkLoadError ? '0.75rem' : '1.5rem',
              padding: '0.8rem 1.2rem',
              fontSize: '1rem',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#fff',
              color: '#673dfe',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Delete data and refresh
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ==============================|| APP - THEME, ROUTER, LOCAL  ||============================== //

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK || '');

const App = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const { pathname } = useLocation();
  const lightweightRoutes = [
    '/',
    '/login',
    '/signup',
    '/code-verification',
    '/forgot-password',
    '/reset-password',
    '/create-password',
    '/create-password-freemium',
    '/create-password-international'
  ];
  const isLightweightRoute = lightweightRoutes.includes(pathname) || pathname.startsWith('/maintenance');
  const skipThirdPartyBoot = pathname === '/' || pathname === '/login';

  useEffect(() => {
    const { reloadedNeeded } = runStorageMaintenance();
    if (reloadedNeeded) {
      window.location.reload();
      return;
    }

    // dispatch(fetchMenu()).then(() => {
    //   setLoading(false);
    // });
    sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (skipThirdPartyBoot) return;

    // Defer third-party scripts so they don't compete with first interaction in login route (especially iOS).
    const timer = window.setTimeout(() => {
      bootThirdPartyScripts();
    }, 1200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [skipThirdPartyBoot]);

  if (loading) return <Loader />;

  const appContent = (
    <>
      <Notistack>
        <OfferProvider>
          <DTDevelopmentProvider>
            <Routes />
            <Snackbar />
          </DTDevelopmentProvider>
        </OfferProvider>
      </Notistack>
    </>
  );

  return (
    <ErrorBoundary>
      <RTLLayout>
        <Locales>
          <ScrollTop>
            <AuthProvider>
              <ThemeCustomization>
                {isLightweightRoute ? (
                  appContent
                ) : (
                  <Elements stripe={stripePromise}>
                    <APIProvider
                      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
                      onLoad={() => console.log('Maps API has loaded.')}
                    >
                      {appContent}
                    </APIProvider>
                  </Elements>
                )}
              </ThemeCustomization>
            </AuthProvider>
          </ScrollTop>
        </Locales>
      </RTLLayout>
    </ErrorBoundary>
  );
};

export default App;
